import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, asApiError, type CoordinatorUpdate } from '../lib/api';
import type { CoordinatorRow } from '../types';
import { DpasBadge } from '../components/DpasBadge';
import { ClaimButton } from '../components/ClaimButton';
import { CommsModal } from '../components/CommsModal';
import styles from './Queue.module.css';

const QUEUE_KEY = ['coordinator', 'queue'];

export function CoordinatorQueue({ meId }: { meId: number }) {
    const queue = useQuery({ queryKey: QUEUE_KEY, queryFn: api.coordinatorQueue });
    const [commsFor, setCommsFor] = useState<CoordinatorRow | null>(null);

    if (queue.isLoading) return <p className={styles.state}>Loading queue…</p>;
    if (queue.isError) return <p className={styles.error}>Failed to load the coordinator queue.</p>;
    const rows = queue.data?.rows ?? [];

    return (
        <div className={styles.screen}>
            <h2 className={styles.heading}>
                Order Coordinator queue <span className={styles.count}>{rows.length} open</span>
            </h2>
            <div className={styles.tableWrap}>
                <table>
                    <thead>
                        <tr>
                            <th>PO</th>
                            <th>Customer</th>
                            <th>DPAS</th>
                            <th>Qty</th>
                            <th>Contract</th>
                            <th>Real need date</th>
                            <th>Status</th>
                            <th>Bus. days</th>
                            <th>Claim</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <Row
                                key={row.line_id}
                                row={row}
                                meId={meId}
                                onComms={() => setCommsFor(row)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            {commsFor && (
                <CommsModal
                    lineId={commsFor.line_id}
                    title={commsFor.po_number}
                    onClose={() => setCommsFor(null)}
                />
            )}
        </div>
    );
}

function Row({ row, meId, onComms }: { row: CoordinatorRow; meId: number; onComms: () => void }) {
    const qc = useQueryClient();
    const mine = row.claimed_by_user_id === meId;
    const [needDate, setNeedDate] = useState(row.real_need_date ?? '');
    const [qty, setQty] = useState<string>(
        row.real_need_qty != null ? String(row.real_need_qty) : '',
    );
    const [error, setError] = useState<string | null>(null);

    const invalidate = () => qc.invalidateQueries({ queryKey: QUEUE_KEY });
    const onError = (e: unknown) => {
        const api = asApiError(e);
        if (api?.status === 409 && api.body?.held_by) {
            setError(`Claimed by ${api.body.held_by.user_name ?? 'another coordinator'}`);
        } else if (api?.status === 409) {
            setError(api.body?.error ?? 'Claim this PO first');
        } else {
            setError('Action failed');
        }
    };

    const claim = useMutation({
        mutationFn: () => api.claim(row.line_id),
        onSuccess: (r) => {
            if (!r.ok && r.held_by) setError(`Claimed by ${r.held_by.user_name}`);
            else setError(null);
            invalidate();
        },
        onError,
    });
    const release = useMutation({
        mutationFn: () => api.release(row.line_id),
        onSuccess: invalidate,
        onError,
    });
    const contact = useMutation({
        mutationFn: () => api.contact(row.line_id),
        onSuccess: invalidate,
        onError,
    });
    const handOff = useMutation({
        mutationFn: () => api.handOff(row.line_id),
        onSuccess: invalidate,
        onError,
    });
    const save = useMutation({
        mutationFn: (body: CoordinatorUpdate) => api.update(row.line_id, body),
        onSuccess: () => {
            setError(null);
            invalidate();
        },
        onError,
    });

    const busy =
        claim.isPending ||
        release.isPending ||
        contact.isPending ||
        handOff.isPending ||
        save.isPending;

    return (
        <tr>
            <td>{row.po_number}</td>
            <td>{row.customer_name ?? '—'}</td>
            <td>
                <DpasBadge rating={row.dpas_rating} />
            </td>
            <td>{row.qty_ordered}</td>
            <td>{row.contract_date}</td>
            <td>
                {mine ? (
                    <span className={styles.inlineEdit}>
                        <input
                            type="date"
                            aria-label={`Real need date for ${row.po_number}`}
                            value={needDate}
                            onChange={(e) => setNeedDate(e.target.value)}
                        />
                        <input
                            type="number"
                            aria-label={`Real need qty for ${row.po_number}`}
                            placeholder="qty"
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                        />
                        <button
                            disabled={busy || !needDate}
                            onClick={() =>
                                save.mutate({
                                    real_need_date: needDate,
                                    ...(qty ? { real_need_qty: Number(qty) } : {}),
                                })
                            }
                        >
                            Save
                        </button>
                    </span>
                ) : (
                    <span className={row.real_need_date ? undefined : styles.muted}>
                        {row.real_need_date ?? 'not set'}
                    </span>
                )}
            </td>
            <td>
                {row.week1_status}
                {row.customer_expedite_flag ? (
                    <span className={styles.expedite}> · EXPEDITE</span>
                ) : null}
            </td>
            <td>{row.days_since_received ?? '—'}</td>
            <td>
                <ClaimButton
                    claimedByUserId={row.claimed_by_user_id}
                    claimedByName={row.claimed_by_name}
                    meId={meId}
                    busy={busy}
                    onClaim={() => claim.mutate()}
                    onRelease={() => release.mutate()}
                />
            </td>
            <td>
                <span className={styles.actions}>
                    <button onClick={onComms}>Comms</button>
                    <button disabled={!mine || busy} onClick={() => contact.mutate()}>
                        Contact
                    </button>
                    <button
                        className="primary"
                        disabled={!mine || busy || !row.real_need_date}
                        title={
                            row.real_need_date
                                ? 'Hand off to Dave'
                                : 'Capture the real need date first'
                        }
                        onClick={() => handOff.mutate()}
                    >
                        Hand off
                    </button>
                </span>
                {error && <span className={styles.rowError}>{error}</span>}
            </td>
        </tr>
    );
}
